/**
 * React Query hooks for equipment and assignees (project-asset-tree-view)
 */

import { useCallback, useState } from 'react';
import { useQuery, useQueryClient, UseQueryResult, QueryClient } from '@tanstack/react-query';
import { equipmentService } from '../../../../../services/equipmentService';
import { normalizeEquipmentId } from '../idUtils';

export interface Equipment {
  _id: string;
  id?: string;
  equipmentName?: string;
  name?: string;
  /** From tree API batch lookup (project asset display name) */
  projectAssetName?: string;
  assetType?: string;
  hierarchyLevel?: string;
  parentId?: string;
  parentEquipmentId?: string;
  projectAssetId?: string;
  assetId?: string;
  /** Direct persisted children count (lazy tree API); not virtual UI nodes */
  childCount?: number;
  siteEngineerId?: string;
  siteEngineerName?: string;
  siteSupervisorId?: string;
  siteSupervisorName?: string;
  [key: string]: unknown;
}

export interface EquipmentQueryParams {
  projectId: string;
  limit?: number;
  sortBy?: string;
  /** Set true only if string rows lack `pvModuleCount` and need legacy slot arrays */
  includePvModuleSlots?: boolean;
}

export interface EquipmentResponse {
  data: Equipment[];
  total?: number;
  page?: number;
  limit?: number;
  meta?: {
    totalDocs?: number;
    mergedPages?: number;
    hasNextPage?: boolean;
    totalPages?: number;
    [key: string]: unknown;
  };
}

export const DEFAULT_EQUIPMENT_TREE_QUERY_LIMIT = 10000;
export const DEFAULT_EQUIPMENT_TREE_SORT_BY = 'hierarchyLevel';

/** Stable query key for the project equipment tree (keep in sync with useEquipmentQuery). */
export function getEquipmentTreeQueryKey(
  projectId: string,
  opts?: { limit?: number; sortBy?: string; includePvModuleSlots?: boolean }
): readonly [
  'equipment',
  string,
  { limit: number; sortBy: string; view: 'tree-lazy'; includePvModuleSlots: boolean }
] {
  const limit = opts?.limit ?? DEFAULT_EQUIPMENT_TREE_QUERY_LIMIT;
  const sortBy = opts?.sortBy ?? DEFAULT_EQUIPMENT_TREE_SORT_BY;
  const includePvModuleSlots = opts?.includePvModuleSlots === true;
  return ['equipment', projectId, { limit, sortBy, view: 'tree-lazy' as const, includePvModuleSlots }];
}

export type ProjectEquipmentSummary = {
  total: number;
  breakdown: Array<{ label: string; count: number }>;
  moduleCount: number;
};

/** Query key for {@link useProjectEquipmentSummaryQuery} — invalidate with `['equipment', projectId]` or this helper. */
export function getProjectEquipmentSummaryQueryKey(
  projectId: string
): readonly ['equipment', string, 'equipment-summary'] {
  return ['equipment', projectId, 'equipment-summary'] as const;
}

/** @deprecated Use {@link getProjectEquipmentSummaryQueryKey} */
export const getProjectEquipmentTotalQueryKey = getProjectEquipmentSummaryQueryKey;

/** Append bulk-created rows so the tree updates without waiting for a full paginated refetch. */
export function mergeCreatedIntoEquipmentTreeCache(
  queryClient: QueryClient,
  projectId: string,
  newRows: Equipment[],
  opts?: { limit?: number; sortBy?: string; includePvModuleSlots?: boolean }
): void {
  if (!newRows.length) return;
  const key = getEquipmentTreeQueryKey(projectId, opts);
  queryClient.setQueryData<EquipmentResponse>(key, (old) => {
    if (!old?.data) return old;
    const byId = new Set(old.data.map((e) => String((e as { _id?: string; id?: string })._id || (e as { id?: string }).id || '')));
    const additions = newRows.filter((r) => {
      const id = (r as { _id?: string })._id != null ? String((r as { _id?: string })._id) : '';
      return id && !byId.has(id);
    });
    if (!additions.length) return old;
    const prevTotal = (old.meta?.totalDocs as number) ?? old.data.length;
    return {
      ...old,
      data: [...old.data, ...additions],
      limit: (old.limit ?? old.data.length) + additions.length,
      meta: {
        ...old.meta,
        totalDocs: prevTotal + additions.length,
      },
    };
  });
  void queryClient.invalidateQueries({ queryKey: getProjectEquipmentSummaryQueryKey(projectId) });
}

/** Merge one equipment document into the lazy tree cache (e.g. full `solarDcLayout` from GET /equipment/:id). */
export function mergeEquipmentRowIntoTreeCache(
  queryClient: QueryClient,
  projectId: string,
  row: Record<string, unknown> & { _id?: string; id?: string },
  opts?: { limit?: number; sortBy?: string; includePvModuleSlots?: boolean }
): void {
  if (!projectId) return;
  const key = getEquipmentTreeQueryKey(projectId, opts);
  const rid = String(row._id ?? row.id ?? '');
  if (!rid) return;
  queryClient.setQueryData<EquipmentResponse>(key, (old) => {
    if (!old?.data) return old;
    let found = false;
    const next = old.data.map((e) => {
      const eid = String((e as { _id?: string })._id ?? (e as { id?: string }).id ?? '');
      if (eid !== rid) return e;
      found = true;
      return { ...e, ...row } as Equipment;
    });
    if (!found) return old;
    return { ...old, data: next };
  });
}

/** Total persisted equipment + asset-wise breakdown (single API; for tree header). */
export function useProjectEquipmentSummaryQuery(projectId: string) {
  return useQuery({
    queryKey: getProjectEquipmentSummaryQueryKey(projectId),
    queryFn: (): Promise<ProjectEquipmentSummary> => equipmentService.getProjectEquipmentSummary(projectId),
    enabled: !!projectId,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
}

export type EquipmentTreeQueryResult = UseQueryResult<EquipmentResponse, Error> & {
  loadChildrenForEquipment: (parentId: string) => Promise<void>;
  loadingChildrenForParentId: string | null;
  hydrateEquipmentRow: (equipmentId: string) => Promise<void>;
  hydratingEquipmentId: string | null;
};

export function useEquipmentQuery(params: EquipmentQueryParams): EquipmentTreeQueryResult {
  const {
    projectId,
    limit = DEFAULT_EQUIPMENT_TREE_QUERY_LIMIT,
    sortBy = DEFAULT_EQUIPMENT_TREE_SORT_BY,
    includePvModuleSlots = false,
  } = params;

  const queryClient = useQueryClient();
  const queryKey = getEquipmentTreeQueryKey(projectId, { limit, sortBy, includePvModuleSlots });
  const [loadingChildrenForParentId, setLoadingChildrenForParentId] = useState<string | null>(null);
  const [hydratingEquipmentId, setHydratingEquipmentId] = useState<string | null>(null);

  const query = useQuery<EquipmentResponse, Error>({
    queryKey,
    queryFn: async () => {
      const roots = (await equipmentService.getProjectTreeNodes(projectId, {
        ...(includePvModuleSlots ? { includePvModuleSlots: true } : {}),
      })) as Equipment[];
      const list = Array.isArray(roots) ? roots : [];
      return {
        data: list,
        total: list.length,
        page: 1,
        limit: list.length,
        meta: {
          totalDocs: list.length,
          mergedPages: 1,
          totalPages: 1,
          hasNextPage: false,
        },
      };
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const loadChildrenForEquipment = useCallback(
    async (parentId: string) => {
      if (!projectId) return;
      const pNorm = normalizeEquipmentId(parentId);
      if (!pNorm) return;

      const current = queryClient.getQueryData<EquipmentResponse>(queryKey);
      const existing = current?.data ?? [];
      const realLoaded = existing.filter((e) => {
        const pp = normalizeEquipmentId(e.parentId ?? (e as { parentEquipmentId?: string }).parentEquipmentId);
        return pp === pNorm;
      }).length;

      const parentRow = existing.find((e) => normalizeEquipmentId(e._id ?? e.id) === pNorm);
      const expectedCount =
        parentRow != null && typeof parentRow.childCount === 'number' ? parentRow.childCount : null;

      if (expectedCount === 0) return;
      if (expectedCount !== null && realLoaded >= expectedCount) return;
      if (expectedCount === null && realLoaded > 0) return;

      setLoadingChildrenForParentId(pNorm);
      try {
        const batch = (await equipmentService.getProjectTreeNodes(projectId, {
          parentId: pNorm,
          ...(includePvModuleSlots ? { includePvModuleSlots: true } : {}),
        })) as Equipment[];
        const rows = Array.isArray(batch) ? batch : [];

        queryClient.setQueryData<EquipmentResponse>(queryKey, (old) => {
          const prev = old?.data ?? [];
          const byId = new Set(prev.map((e) => String((e as { _id?: string })._id || (e as { id?: string }).id || '')));
          const additions = rows.filter((r) => {
            const id = (r as { _id?: string })._id != null ? String((r as { _id?: string })._id) : '';
            return id && !byId.has(id);
          });
          if (!additions.length) return old;
          const nextList = [...prev, ...additions];
          return {
            ...old,
            data: nextList,
            total: nextList.length,
            limit: nextList.length,
            meta: {
              ...old?.meta,
              totalDocs: nextList.length,
            },
          };
        });
      } finally {
        setLoadingChildrenForParentId(null);
      }
    },
    [projectId, queryClient, queryKey, includePvModuleSlots]
  );

  const hydrateEquipmentRow = useCallback(
    async (equipmentId: string) => {
      if (!projectId) return;
      const idNorm = normalizeEquipmentId(equipmentId);
      if (!idNorm) return;
      setHydratingEquipmentId(idNorm);
      try {
        const row = await equipmentService.getById(idNorm);
        if (row && typeof row === 'object') {
          mergeEquipmentRowIntoTreeCache(queryClient, projectId, row as Record<string, unknown> & { _id?: string }, {
            limit,
            sortBy,
            includePvModuleSlots,
          });
        }
      } finally {
        setHydratingEquipmentId(null);
      }
    },
    [projectId, queryClient, limit, sortBy, includePvModuleSlots]
  );

  return {
    ...query,
    loadChildrenForEquipment,
    loadingChildrenForParentId,
    hydrateEquipmentRow,
    hydratingEquipmentId,
  };
}

export function useAssigneesQuery(projectId: string) {
  return useQuery({
    queryKey: ['assignees', projectId],
    queryFn: async () => {
      const { assignmentService } = await import('../../../../../services/assignmentService');
      const response = await assignmentService.getEngineersWithAssignments({ projectId });
      return response?.engineers || [];
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000
  });
}
